import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { Event } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCompanyData,
  useContentToken,
  useSlotGroup,
  useVibesiteEvents,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ExternalLink,
  HeartHandshake,
  MapPin,
  Network,
  Plus,
  RotateCcw,
  Users,
} from 'lucide-react';
import {
  getCommunityPath,
  getCommunityProperties,
  getLatLng,
  normalizeAbbr,
} from './propertyUtils';
import {
  FootprintMap,
  MAP_H,
  MAP_LAT_MAX,
  MAP_LAT_MIN,
  MAP_LNG_MAX,
  MAP_LNG_MIN,
  MAP_W,
  StateButton,
  type MapPoint,
} from './InvestorsFootprintMap';

const portalCtaDef: SlotGroupDefinition = {
  label: 'Investor Portal Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/investors' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Investor Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const strategyLinkDef: SlotGroupDefinition = {
  label: 'Strategy Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/strategy' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore our full strategy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const panelImage1Def: SlotGroupDefinition = {
  label: 'Who We Are Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'American Landmark community' },
  },
};

const panelImage2Def: SlotGroupDefinition = {
  label: 'What We Do Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Renovated apartment community' },
  },
};

const panelImage3Def: SlotGroupDefinition = {
  label: 'Our Strategy Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sun Belt multifamily investment' },
  },
};

const peopleImageDef: SlotGroupDefinition = {
  label: 'People Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'American Landmark team members' },
  },
};

const valueMetric1Def: SlotGroupDefinition = {
  label: 'Value-Add Metric 1',
  fields: {
    label: { label: 'Metric', type: 'text', defaultValue: 'Average monthly rent' },
    before: { label: 'At Acquisition', type: 'text', defaultValue: '$1,210' },
    after: { label: 'Stabilized', type: 'text', defaultValue: '$1,445' },
    change: { label: 'Change Badge', type: 'text', defaultValue: '+19%' },
    beforeWidth: { label: 'At Acquisition Bar Width (0–100)', type: 'text', defaultValue: '72' },
    afterWidth: { label: 'Stabilized Bar Width (0–100)', type: 'text', defaultValue: '86' },
  },
};

const valueMetric2Def: SlotGroupDefinition = {
  label: 'Value-Add Metric 2',
  fields: {
    label: { label: 'Metric', type: 'text', defaultValue: 'Physical occupancy' },
    before: { label: 'At Acquisition', type: 'text', defaultValue: '89%' },
    after: { label: 'Stabilized', type: 'text', defaultValue: '95%' },
    change: { label: 'Change Badge', type: 'text', defaultValue: '+6 pts' },
    beforeWidth: { label: 'At Acquisition Bar Width (0–100)', type: 'text', defaultValue: '89' },
    afterWidth: { label: 'Stabilized Bar Width (0–100)', type: 'text', defaultValue: '95' },
  },
};

const valueMetric3Def: SlotGroupDefinition = {
  label: 'Value-Add Metric 3',
  fields: {
    label: { label: 'Metric', type: 'text', defaultValue: 'Net operating income (indexed)' },
    before: { label: 'At Acquisition', type: 'text', defaultValue: '100' },
    after: { label: 'Stabilized', type: 'text', defaultValue: '128' },
    change: { label: 'Change Badge', type: 'text', defaultValue: '+28%' },
    beforeWidth: { label: 'At Acquisition Bar Width (0–100)', type: 'text', defaultValue: '66' },
    afterWidth: { label: 'Stabilized Bar Width (0–100)', type: 'text', defaultValue: '85' },
  },
};

const state1Def: SlotGroupDefinition = {
  label: 'State 1',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Texas' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'TX' },
    region: { label: 'Region', type: 'text', defaultValue: 'Texas Triangle' },
    markets: {
      label: 'Key Markets',
      type: 'text',
      defaultValue: 'Dallas–Fort Worth · Houston · San Antonio · Austin',
    },
  },
};

const state2Def: SlotGroupDefinition = {
  label: 'State 2',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Florida' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'FL' },
    region: { label: 'Region', type: 'text', defaultValue: 'Florida' },
    markets: {
      label: 'Key Markets',
      type: 'text',
      defaultValue: 'Tampa Bay · Orlando · Jacksonville · South Florida · Space Coast',
    },
  },
};

const state3Def: SlotGroupDefinition = {
  label: 'State 3',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'North Carolina' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'NC' },
    region: { label: 'Region', type: 'text', defaultValue: 'Carolinas' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Charlotte · Raleigh–Durham' },
  },
};

const state4Def: SlotGroupDefinition = {
  label: 'State 4',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Georgia' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'GA' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southeast' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Atlanta' },
  },
};

const state5Def: SlotGroupDefinition = {
  label: 'State 5',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'South Carolina' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'SC' },
    region: { label: 'Region', type: 'text', defaultValue: 'Carolinas' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Charleston' },
  },
};

const state6Def: SlotGroupDefinition = {
  label: 'State 6',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Tennessee' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'TN' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southeast' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Nashville' },
  },
};

const state7Def: SlotGroupDefinition = {
  label: 'State 7',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Virginia' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'VA' },
    region: { label: 'Region', type: 'text', defaultValue: 'Mid-Atlantic' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Richmond' },
  },
};

const state8Def: SlotGroupDefinition = {
  label: 'State 8',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Arizona' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'AZ' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southwest' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Phoenix' },
  },
};

const milestone1Def: SlotGroupDefinition = {
  label: 'Milestone 1',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '1996' },
    title: { label: 'Title', type: 'text', defaultValue: 'Roots in multifamily' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Our founding leadership begins acquiring, renovating, and operating apartment communities — the start of a cumulative management track record of more than 100,000 units.',
    },
  },
};

const milestone2Def: SlotGroupDefinition = {
  label: 'Milestone 2',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2016' },
    title: { label: 'Title', type: 'text', defaultValue: 'American Landmark is founded' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Following the successful sale of Landmark Apartment Trust, the team launches a vertically-integrated owner-operator focused on the Sun Belt.',
    },
  },
};

const milestone3Def: SlotGroupDefinition = {
  label: 'Milestone 3',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2017' },
    title: { label: 'Title', type: 'text', defaultValue: 'Fund I launches' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Our first closed-end, value-add multifamily fund launches — and has since been fully realized.',
    },
  },
};

const milestone4Def: SlotGroupDefinition = {
  label: 'Milestone 4',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2022' },
    title: { label: 'Title', type: 'text', defaultValue: 'Growing the Sun Belt footprint' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Entry into Arizona with The Access in Tempe extends our platform from the Southeast and Texas into the Southwest.',
    },
  },
};

const milestone5Def: SlotGroupDefinition = {
  label: 'Milestone 5',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2024' },
    title: { label: 'Title', type: 'text', defaultValue: 'Fund IV closes at $1.04B' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Exceeding its $975M target, with commitments from pension funds, sovereign wealth funds, endowments, and insurance companies.',
    },
  },
};

const milestone6Def: SlotGroupDefinition = {
  label: 'Milestone 6',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2026' },
    title: { label: 'Title', type: 'text', defaultValue: 'Fund V first close' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Approximately $400M first close for American Landmark Fund V, our fifth fund, targeting ~$1B in total commitments.',
    },
  },
};

const INVESTOR_TYPES = [
  'Public Pension',
  'Private Pension',
  'Multi-Family Office',
  'Single-Family Office',
  'High-Net-Worth Individual',
  'Other',
] as const;

const EYEBROW_CLASS = 'font-heading text-sm font-semibold uppercase tracking-[0.14em]';
const H2_CLASS =
  'font-heading text-[clamp(32px,4vw,58px)] font-semibold leading-[1.04] tracking-[-0.03em]';
const inputClass =
  'w-full rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 font-body text-base text-[rgb(var(--ala-text))] outline-none transition placeholder:text-[rgb(var(--ala-text-muted))]/70 focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20';
const labelClass = 'mb-2 block font-heading text-sm font-semibold text-[rgb(var(--ala-text))]';
const STROKE_TEXT = '[-webkit-text-stroke:1.5px_currentColor] text-transparent';

type SlotData = Record<string, string | null | undefined>;
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

function Eyebrow({ light = false, children }: { light?: boolean; children: ReactNode }) {
  return (
    <div className="mb-5 flex items-center gap-2">
      <span className={`h-2 w-2 shrink-0 rounded-[3px] ${light ? 'bg-[rgb(var(--ala-secondary))]' : 'bg-[rgb(var(--ala-accent))]'}`} aria-hidden />
      {children}
    </div>
  );
}

function trackSpotlight(e: MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`);
}

function Spotlight({ strength = 0.18 }: { strength?: number }) {
  return (
    <span
      className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      style={{
        background: `radial-gradient(340px circle at var(--spot-x, 50%) var(--spot-y, 50%), rgba(84,158,255,${strength}), transparent 65%)`,
      }}
      aria-hidden
    />
  );
}

function PortalButton({ data, variant = 'solid' }: { data: SlotData; variant?: 'solid' | 'outline-light' }) {
  const styles =
    variant === 'solid'
      ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))] hover:shadow-[0_10px_30px_rgba(84,158,255,0.45)]'
      : 'border border-[rgb(var(--ala-on-primary))]/40 text-[rgb(var(--ala-on-primary))] hover:border-[rgb(var(--ala-secondary))] hover:bg-[rgb(var(--ala-secondary))]/10';
  return (
    <a
      href={data.url || 'https://www.alapts.com/investors'}
      target={data._target || '_blank'}
      rel="noopener noreferrer"
      className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-heading text-sm font-semibold uppercase tracking-wide transition-all duration-300 hover:-translate-y-0.5 ${styles}`}
    >
      {data.label || 'Investor Portal'}
      <ExternalLink className="h-4 w-4 transition-transform group-hover:rotate-12" aria-hidden />
    </a>
  );
}

function StrategyPanel({
  index,
  active,
  onActivate,
  image,
  dark = false,
  children,
}: {
  index: number;
  active: boolean;
  onActivate: () => void;
  image: ReactNode;
  dark?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={active}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      className={`group relative flex min-h-[440px] cursor-pointer flex-col justify-end overflow-hidden rounded-3xl outline-none ring-[rgb(var(--ala-secondary))] transition-[flex-grow] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:ring-2 lg:min-h-0 lg:basis-0 ${
        active ? 'lg:grow-[3.2]' : 'lg:grow'
      }`}
    >
      <div className="absolute inset-0">{image}</div>
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          dark
            ? 'bg-[linear-gradient(180deg,rgb(var(--ala-primary)/0.35)_0%,rgb(var(--ala-primary)/0.95)_70%)]'
            : 'bg-[linear-gradient(180deg,rgb(var(--ala-dark)/0.1)_0%,rgb(var(--ala-dark)/0.9)_75%)]'
        } ${active ? 'opacity-100' : 'opacity-90'}`}
      />
      <div className="relative z-10 flex flex-col gap-4 p-7 md:p-9">
        <div className="flex items-center justify-between gap-4">
          <span className="font-heading text-sm font-semibold tabular-nums text-[rgb(var(--ala-secondary))]">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/30 text-[rgb(var(--ala-on-primary))] transition-transform duration-500 ${
              active ? 'rotate-45 bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]' : ''
            }`}
            aria-hidden
          >
            <Plus className="h-4 w-4" />
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

function parseMetric(value: string | null | undefined) {
  const match = (value || '').match(/^(.*?)(-?[\d,]*\.?\d+)(.*)$/);
  if (!match) return null;
  const digits = match[2];
  return {
    prefix: match[1],
    num: Number.parseFloat(digits.replace(/,/g, '')),
    suffix: match[3],
    commas: digits.includes(','),
    decimals: (digits.split('.')[1] || '').length,
  };
}

function lerpMetric(before: string | null | undefined, after: string | null | undefined, t: number): string {
  const a = parseMetric(before);
  const b = parseMetric(after);
  if (!a || !b) return (t < 0.5 ? before : after) || '';
  const n = a.num + (b.num - a.num) * t;
  const formatted = b.commas
    ? n.toLocaleString('en-US', { minimumFractionDigits: b.decimals, maximumFractionDigits: b.decimals })
    : n.toFixed(b.decimals);
  return `${b.prefix}${formatted}${b.suffix}`;
}

function clampPct(value: string | null | undefined): number {
  const n = Number.parseFloat(value || '');
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 100) : 0;
}

function ValueMetric({ data, t }: { data: SlotData; t: number }) {
  const beforePct = clampPct(data.beforeWidth);
  const afterPct = clampPct(data.afterWidth);
  const width = beforePct + (afterPct - beforePct) * t;
  return (
    <div className="flex flex-col gap-3 border-t border-[rgb(var(--ala-on-primary))]/10 pt-5 first:border-t-0 first:pt-0">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="block font-heading text-sm text-[rgb(var(--ala-on-primary))]/60">{data.label}</span>
          <span className="mt-1 block font-heading text-[clamp(26px,2.6vw,36px)] font-semibold leading-none tabular-nums text-[rgb(var(--ala-on-primary))]">
            {lerpMetric(data.before, data.after, t)}
          </span>
        </div>
        {data.change ? (
          <span
            className="rounded-full bg-[rgb(var(--ala-secondary))]/15 px-3 py-1 font-heading text-sm font-semibold text-[rgb(var(--ala-secondary))] transition-all duration-300"
            style={{ opacity: t > 0.95 ? 1 : 0.15, transform: `scale(${t > 0.95 ? 1 : 0.9})` }}
          >
            {data.change}
          </span>
        ) : null}
      </div>
      <div className="relative h-2.5 rounded-full bg-[rgb(var(--ala-on-primary))]/[0.06]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[rgb(var(--ala-secondary))]/40 to-[rgb(var(--ala-secondary))]"
          style={{ width: `${width}%` }}
        />
        <span
          className="absolute -top-1 h-[18px] w-[2px] rounded bg-[rgb(var(--ala-on-primary))]/50"
          style={{ left: `${beforePct}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function ValueAddSimulator({
  stages,
  wrapStages,
  startLabel,
  endLabel,
  children,
}: {
  stages: string[];
  wrapStages: (node: ReactNode) => ReactNode;
  startLabel: ReactNode;
  endLabel: ReactNode;
  children: (t: number) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-20% 0px' });
  const reduceMotion = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const frame = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  const play = useCallback(() => {
    stop();
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / 2600, 1);
      setProgress((1 - Math.pow(1 - t, 3)) * 100);
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }, [stop]);

  useEffect(() => {
    if (!inView) return;
    if (reduceMotion) setProgress(100);
    else play();
  }, [inView, reduceMotion, play]);

  useEffect(() => stop, [stop]);

  const t = progress / 100;
  const activeStage = stages.length > 1 ? Math.round(t * (stages.length - 1)) : 0;

  return (
    <div ref={ref} className="flex flex-col gap-7">
      <div className="flex flex-col gap-5">{children(t)}</div>

      <div className="rounded-2xl bg-[rgb(var(--ala-on-primary))]/[0.04] p-5">
        <div className="mb-3 flex items-center justify-between font-heading text-xs font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-on-primary))]/60">
          <span>{startLabel}</span>
          <button
            type="button"
            onClick={play}
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))]/10"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Replay
          </button>
          <span>{endLabel}</span>
        </div>
        <div className="relative flex h-8 items-center">
          <div className="absolute inset-x-0 h-1.5 rounded-full bg-[rgb(var(--ala-on-primary))]/10" />
          <div className="absolute left-0 h-1.5 rounded-full bg-[rgb(var(--ala-secondary))]" style={{ width: `${progress}%` }} />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(progress)}
            onChange={(e) => {
              stop();
              setProgress(Number(e.target.value));
            }}
            aria-label="Value-add business plan progress"
            className="relative z-10 h-8 w-full cursor-grab appearance-none bg-transparent active:cursor-grabbing [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_6px_rgba(84,158,255,0.35)] [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110"
          />
        </div>
        {wrapStages(
        <div className="mt-3 flex justify-between gap-2">
          {stages.map((stage, i) => (
            <button
              key={`${stage}-${i}`}
              type="button"
              onClick={() => {
                stop();
                setProgress(stages.length > 1 ? (i / (stages.length - 1)) * 100 : 100);
              }}
              className={`font-heading text-xs font-semibold uppercase tracking-wide transition-colors md:text-sm ${
                i === activeStage ? 'text-[rgb(var(--ala-secondary))]' : 'text-[rgb(var(--ala-on-primary))]/40 hover:text-[rgb(var(--ala-on-primary))]/80'
              }`}
            >
              {stage}
            </button>
          ))}
        </div>,
        )}
      </div>
    </div>
  );
}

function PeopleCard({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div
      onMouseMove={trackSpotlight}
      className="group relative flex flex-col gap-4 overflow-hidden rounded-3xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[rgb(var(--ala-secondary))]/40 hover:shadow-[0_18px_50px_rgba(10,22,38,0.1)]"
    >
      <Spotlight strength={0.12} />
      <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgb(var(--ala-secondary))]/10 text-[rgb(var(--ala-secondary))] transition-all duration-500 group-hover:rotate-[-8deg] group-hover:bg-[rgb(var(--ala-secondary))] group-hover:text-[rgb(var(--ala-on-secondary))]">
        {icon}
      </div>
      <div className="relative z-10 flex flex-col gap-2">{children}</div>
    </div>
  );
}

function Timeline({
  milestones,
  renderYearButton,
}: {
  milestones: SlotData[];
  renderYearButton: (index: number, button: ReactNode) => ReactNode;
}) {
  const [active, setActive] = useState(0);
  const current = milestones[active] || {};
  const last = milestones.length - 1;
  const go = (i: number) => setActive(Math.min(Math.max(i, 0), last));

  return (
    <div>
      <div className="-mx-6 overflow-x-auto px-6 pb-2 pt-3 [scrollbar-width:none] md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden">
        <div className="relative min-w-[640px]">
          <div className="absolute left-[calc(100%/12)] right-[calc(100%/12)] top-[7px] h-[2px] bg-[rgb(var(--ala-on-primary))]/10">
            <div
              className="h-full bg-[rgb(var(--ala-secondary))] transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ width: `${last > 0 ? (active / last) * 100 : 100}%` }}
            />
          </div>
          <div className="relative grid" style={{ gridTemplateColumns: `repeat(${milestones.length}, minmax(0, 1fr))` }}>
            {milestones.map((m, i) =>
              renderYearButton(
                i,
                <button
                  type="button"
                  onClick={() => go(i)}
                  aria-current={i === active ? 'step' : undefined}
                  className="group flex w-full flex-col items-center gap-3"
                >
                  <span
                    className={`h-4 w-4 rounded-full border-2 transition-all duration-500 ${
                      i <= active
                        ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))] shadow-[0_0_0_6px_rgba(84,158,255,0.2)]'
                        : 'border-[rgb(var(--ala-on-primary))]/30 bg-[rgb(var(--ala-dark))] group-hover:border-[rgb(var(--ala-on-primary))]/70'
                    } ${i === active ? 'scale-125' : ''}`}
                  />
                  <span
                    className={`font-heading text-lg font-semibold tabular-nums transition-colors ${
                      i === active ? 'text-[rgb(var(--ala-on-primary))]' : 'text-[rgb(var(--ala-on-primary))]/40 group-hover:text-[rgb(var(--ala-on-primary))]/80'
                    }`}
                  >
                    {m.year}
                  </span>
                </button>,
              ),
            )}
          </div>
        </div>
      </div>

      <div className="mt-12 grid gap-8 rounded-3xl border border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-on-primary))]/[0.03] p-7 md:p-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={`year-${active}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className={`block font-heading text-[clamp(88px,13vw,190px)] font-bold leading-[0.85] tracking-[-0.05em] text-[rgb(var(--ala-secondary))] ${STROKE_TEXT}`}
          >
            {current.year}
          </motion.span>
        </AnimatePresence>
        <div className="flex flex-col gap-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`copy-${active}`}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="flex min-h-[150px] flex-col gap-3"
            >
              <h3 className="font-heading text-[clamp(24px,2.4vw,34px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]">
                {current.title}
              </h3>
              <p className="max-w-[560px] font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/70">{current.body}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go(active - 1)}
              disabled={active === 0}
              aria-label="Previous milestone"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/25 text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10 disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go(active + 1)}
              disabled={active === last}
              aria-label="Next milestone"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/25 text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10 disabled:opacity-30"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
            <span className="ml-2 font-heading text-sm tabular-nums text-[rgb(var(--ala-on-primary))]/50">
              {String(active + 1).padStart(2, '0')} / {String(milestones.length).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildLeadPayload(
  name: string,
  email: string,
  phone: string,
  details: string[],
  message: string,
): Record<string, unknown> {
  const nameParts = name.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  return {
    first_name: firstName,
    last_name: nameParts.slice(1).join(' ') || firstName,
    email: email.trim(),
    phone_no: phone.trim(),
    comments: [...details, '', message.trim()].join('\n'),
    message: message.trim(),
  };
}

function InvestorContactForm() {
  const { emit } = useVibesiteEvents();
  const formEmail = useContentToken('investorscopycontent.form_email');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [investorType, setInvestorType] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;
    if (!name.trim() || !email.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');

    const details = [
      `Inquiry: Capital Markets / Investor Relations`,
      investorType ? `Investor type: ${investorType}` : '',
      organization.trim() ? `Organization: ${organization.trim()}` : '',
    ].filter(Boolean);
    const payload = buildLeadPayload(name, email, phone, details, message);

    try {
      const leads = (
        window as unknown as {
          __mh_leads__?: {
            submitAsync?: (data: Record<string, unknown>, opts?: object) => Promise<{ success?: boolean }>;
          };
        }
      ).__mh_leads__;

      if (leads?.submitAsync) {
        const response = await leads.submitAsync(payload, {});
        if (response?.success !== true) throw new Error('Lead submission failed');
      } else if (formEmail) {
        const subject = encodeURIComponent(`Investor inquiry — ${name.trim()}`);
        const body = encodeURIComponent(
          [`Name: ${name.trim()}`, `Email: ${email.trim()}`, `Phone: ${phone.trim()}`, ...details, '', message.trim()].join(
            '\n',
          ),
        );
        window.location.href = `mailto:${formEmail}?subject=${subject}&body=${body}`;
      } else {
        throw new Error('Form is not configured');
      }

      try {
        emit(Event.contact_form_submitted, {
          sectionId: 'investors-copy-content',
          formId: 'investor-contact-form',
          formFields: { investorType },
        });
      } catch {
        // Tracking must never break form UX.
      }

      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setOrganization('');
      setInvestorType('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center rounded-2xl bg-[rgb(var(--ala-surface))] px-6 py-14 text-center"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <p className="mt-5 font-heading text-xl font-semibold text-[rgb(var(--ala-text))]">Thank you.</p>
        <p className="mt-2 font-body text-[rgb(var(--ala-text-muted))]">
          A member of our capital markets team will be in touch shortly.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 font-heading text-sm font-semibold uppercase tracking-wider text-[rgb(var(--ala-secondary))] transition hover:text-[rgb(var(--ala-primary))]"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      <div>
        <label htmlFor="investor-type" className={labelClass}>
          Investor type
        </label>
        <div className="relative">
          <select
            id="investor-type"
            value={investorType}
            onChange={(e) => setInvestorType(e.target.value)}
            disabled={status === 'submitting'}
            className={`${inputClass} appearance-none pr-10`}
          >
            <option value="">Select an investor type</option>
            {INVESTOR_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[rgb(var(--ala-text-muted))]"
            aria-hidden
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="investor-name" className={labelClass}>
            Full Name
          </label>
          <input
            id="investor-name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="investor-org" className={labelClass}>
            Organization
          </label>
          <input
            id="investor-org"
            type="text"
            autoComplete="organization"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="investor-email" className={labelClass}>
            Email Address
          </label>
          <input
            id="investor-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="investor-phone" className={labelClass}>
            Phone Number
          </label>
          <input
            id="investor-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="investor-message" className={labelClass}>
          How can we help?
        </label>
        <textarea
          id="investor-message"
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={status === 'submitting'}
          className={`${inputClass} min-h-[120px] resize-y`}
        />
      </div>

      {status === 'error' ? (
        <p className="font-body text-sm text-[rgb(var(--ala-accent))]" role="alert">
          Please include your name and email. If the problem continues, email us directly.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-[rgb(var(--ala-on-secondary))] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(84,158,255,0.45)] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === 'submitting' ? 'Sending…' : 'Contact Capital Markets'}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
      </button>
    </form>
  );
}

export function InvestorsCopyContent() {
  const companyData = useCompanyData();
  const portal = useSlotGroup('investorscopycontent.portal_cta', portalCtaDef);
  const strategyLink = useSlotGroup('investorscopycontent.strategy_link', strategyLinkDef);
  const panelImage1 = useSlotGroup('investorscopycontent.panel_image_1', panelImage1Def);
  const panelImage2 = useSlotGroup('investorscopycontent.panel_image_2', panelImage2Def);
  const panelImage3 = useSlotGroup('investorscopycontent.panel_image_3', panelImage3Def);
  const peopleImage = useSlotGroup('investorscopycontent.people_image', peopleImageDef);
  const metric1 = useSlotGroup('investorscopycontent.value_metric_1', valueMetric1Def);
  const metric2 = useSlotGroup('investorscopycontent.value_metric_2', valueMetric2Def);
  const metric3 = useSlotGroup('investorscopycontent.value_metric_3', valueMetric3Def);
  const state1 = useSlotGroup('investorscopycontent.state_1', state1Def);
  const state2 = useSlotGroup('investorscopycontent.state_2', state2Def);
  const state3 = useSlotGroup('investorscopycontent.state_3', state3Def);
  const state4 = useSlotGroup('investorscopycontent.state_4', state4Def);
  const state5 = useSlotGroup('investorscopycontent.state_5', state5Def);
  const state6 = useSlotGroup('investorscopycontent.state_6', state6Def);
  const state7 = useSlotGroup('investorscopycontent.state_7', state7Def);
  const state8 = useSlotGroup('investorscopycontent.state_8', state8Def);
  const milestone1 = useSlotGroup('investorscopycontent.milestone_1', milestone1Def);
  const milestone2 = useSlotGroup('investorscopycontent.milestone_2', milestone2Def);
  const milestone3 = useSlotGroup('investorscopycontent.milestone_3', milestone3Def);
  const milestone4 = useSlotGroup('investorscopycontent.milestone_4', milestone4Def);
  const milestone5 = useSlotGroup('investorscopycontent.milestone_5', milestone5Def);
  const milestone6 = useSlotGroup('investorscopycontent.milestone_6', milestone6Def);
  const formEmail = useContentToken('investorscopycontent.form_email');
  const stagesToken = useContentToken('investorscopycontent.value_stages');

  const [activePanel, setActivePanel] = useState(0);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<string | null>(null);

  const stages = useMemo(
    () =>
      (stagesToken || 'Acquire | Renovate | Lease-up | Stabilize')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    [stagesToken],
  );

  const mapPoints = useMemo(() => {
    const points: MapPoint[] = [];
    for (const prop of getCommunityProperties(companyData)) {
      const ll = getLatLng(prop);
      if (!ll || !ll.lat || !ll.lng) continue;
      if (ll.lng < MAP_LNG_MIN || ll.lng > MAP_LNG_MAX || ll.lat < MAP_LAT_MIN || ll.lat > MAP_LAT_MAX) continue;
      points.push({
        id: String(prop.multihub_property_id || prop.property_slug || points.length),
        name: String(prop.property_name || ''),
        city: String(prop.property_address_city || ''),
        abbr: normalizeAbbr(String(prop.property_address_state || '').trim()),
        href: getCommunityPath(prop),
        x: ((ll.lng - MAP_LNG_MIN) / (MAP_LNG_MAX - MAP_LNG_MIN)) * MAP_W,
        y: ((MAP_LAT_MAX - ll.lat) / (MAP_LAT_MAX - MAP_LAT_MIN)) * MAP_H,
      });
    }
    return points;
  }, [companyData]);

  const communityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of mapPoints) counts[p.abbr] = (counts[p.abbr] || 0) + 1;
    return counts;
  }, [mapPoints]);

  const mapFocus = previewState ?? selectedState;
  const abbrOf = (data: SlotData) => (data.abbr || '').trim().toUpperCase();
  const stateProps = (data: SlotData) => {
    const abbr = abbrOf(data);
    return {
      data,
      count: communityCounts[abbr] || 0,
      active: mapFocus === abbr,
      onSelect: () => setSelectedState((cur) => (cur === abbr ? null : abbr)),
      onPreview: (on: boolean) => setPreviewState(on ? abbr : null),
    };
  };

  const panelBodyClass = (i: number) =>
    `grid transition-all duration-500 ${
      activePanel === i ? 'lg:grid-rows-[1fr] lg:opacity-100' : 'lg:grid-rows-[0fr] lg:opacity-0'
    }`;

  return (
    <>
      {/* Who we are / What we do / Our strategy */}
      <section id="strategy" className="w-full bg-[rgb(var(--ala-background))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-end">
            <div>
              <Eyebrow>
                <RichTextContent
                  path="investorscopycontent.strategy_eyebrow"
                  label="Strategy Eyebrow"
                  as="span"
                  className={`${EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
                />
              </Eyebrow>
              <RichTextContent
                path="investorscopycontent.strategy_headline"
                label="Strategy Headline"
                as="h2"
                className={`${H2_CLASS} text-[rgb(var(--ala-text))] [&_em]:not-italic [&_em]:text-[rgb(var(--ala-secondary))]`}
              />
            </div>
            <RichTextContent
              path="investorscopycontent.strategy_intro"
              label="Strategy Intro"
              as="p"
              className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))]"
            />
          </div>

          <div className="mt-12 flex flex-col gap-4 lg:h-[560px] lg:flex-row">
            <StrategyPanel
              index={0}
              active={activePanel === 0}
              onActivate={() => setActivePanel(0)}
              image={
                <EditableSlotGroup groupPath="investorscopycontent.panel_image_1" definition={panelImage1Def} className="block h-full w-full">
                  <VibesitesImage
                    url={panelImage1.image || ''}
                    alt={panelImage1.alt || ''}
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                  />
                </EditableSlotGroup>
              }
            >
              <RichTextContent
                path="investorscopycontent.who_title"
                label="Who We Are Title"
                as="h3"
                className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
              />
              <div className={panelBodyClass(0)}>
                <div className="overflow-hidden">
                  <RichTextContent
                    path="investorscopycontent.who_body"
                    label="Who We Are Body"
                    as="div"
                    className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </div>
            </StrategyPanel>
            <StrategyPanel
              index={1}
              active={activePanel === 1}
              onActivate={() => setActivePanel(1)}
              image={
                <EditableSlotGroup groupPath="investorscopycontent.panel_image_2" definition={panelImage2Def} className="block h-full w-full">
                  <VibesitesImage
                    url={panelImage2.image || ''}
                    alt={panelImage2.alt || ''}
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                  />
                </EditableSlotGroup>
              }
            >
              <RichTextContent
                path="investorscopycontent.what_title"
                label="What We Do Title"
                as="h3"
                className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
              />
              <div className={panelBodyClass(1)}>
                <div className="overflow-hidden">
                  <RichTextContent
                    path="investorscopycontent.what_body"
                    label="What We Do Body"
                    as="div"
                    className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </div>
            </StrategyPanel>
            <StrategyPanel
              index={2}
              active={activePanel === 2}
              onActivate={() => setActivePanel(2)}
              dark
              image={
                <EditableSlotGroup groupPath="investorscopycontent.panel_image_3" definition={panelImage3Def} className="block h-full w-full">
                  <VibesitesImage
                    url={panelImage3.image || ''}
                    alt={panelImage3.alt || ''}
                    sizes="(max-width: 1024px) 100vw, 60vw"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                  />
                </EditableSlotGroup>
              }
            >
              <RichTextContent
                path="investorscopycontent.how_title"
                label="Our Strategy Title"
                as="h3"
                className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
              />
              <div className={panelBodyClass(2)}>
                <div className="overflow-hidden">
                  <RichTextContent
                    path="investorscopycontent.how_body"
                    label="Our Strategy Body"
                    as="div"
                    className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </div>
            </StrategyPanel>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <EditableSlotGroup groupPath="investorscopycontent.portal_cta" definition={portalCtaDef}>
              <PortalButton data={portal} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="investorscopycontent.strategy_link" definition={strategyLinkDef}>
              <a
                href={strategyLink.url || '/strategy'}
                target={strategyLink._target || '_self'}
                className="group inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]"
              >
                {strategyLink.label || 'Explore our full strategy'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      {/* Value-add spotlight */}
      <section className="relative w-full overflow-hidden bg-[rgb(var(--ala-dark))] py-20 md:py-28">
        <div
          className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[rgb(var(--ala-secondary))]/10 blur-[140px]"
          aria-hidden
        />
        <div className="relative mx-auto grid max-w-[1440px] gap-12 px-6 md:px-[clamp(16px,3.3vw,48px)] lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center lg:gap-16">
          <div>
            <Eyebrow light>
              <RichTextContent
                path="investorscopycontent.value_eyebrow"
                label="Value-Add Eyebrow"
                as="span"
                className={`${EYEBROW_CLASS} text-[rgb(var(--ala-on-primary))]/70`}
              />
            </Eyebrow>
            <RichTextContent
              path="investorscopycontent.value_headline"
              label="Value-Add Headline"
              as="h2"
              className={`${H2_CLASS} text-[rgb(var(--ala-on-primary))] [&_em]:not-italic [&_em]:text-[rgb(var(--ala-secondary))]`}
            />
            <RichTextContent
              path="investorscopycontent.value_body"
              label="Value-Add Body"
              as="p"
              className="mt-6 max-w-[560px] font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/75"
            />
            <RichTextContent
              path="investorscopycontent.value_levers"
              label="Value-Add Levers"
              as="div"
              className="mt-8 font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_li]:relative [&_li]:border-t [&_li]:border-[rgb(var(--ala-on-primary))]/10 [&_li]:py-3 [&_li]:pl-6 [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[1.2rem] [&_li]:before:h-2 [&_li]:before:w-2 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))] [&_ul]:list-none"
            />
            <div className="mt-8">
              <PortalButton data={portal} variant="outline-light" />
            </div>
          </div>

          <div className="rounded-3xl border border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-primary))]/80 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur md:p-9">
            <div className="mb-7 flex flex-col gap-2">
              <RichTextContent
                path="investorscopycontent.chart_title"
                label="Chart Title"
                as="h3"
                className="font-heading text-xl font-semibold text-[rgb(var(--ala-on-primary))]"
              />
              <RichTextContent
                path="investorscopycontent.value_hint"
                label="Chart Hint"
                as="p"
                className="font-body text-sm text-[rgb(var(--ala-on-primary))]/55"
              />
            </div>
            <ValueAddSimulator
                stages={stages}
                wrapStages={(node) => (
                  <EditableToken path="investorscopycontent.value_stages" label="Plan Stages (separate with |)" as="div">
                    {node}
                  </EditableToken>
                )}
                startLabel={<RichTextContent path="investorscopycontent.chart_before_label" label="Chart Start Label" as="span" />}
                endLabel={<RichTextContent path="investorscopycontent.chart_after_label" label="Chart End Label" as="span" />}
              >
                {(t) => (
                  <>
                    <EditableSlotGroup groupPath="investorscopycontent.value_metric_1" definition={valueMetric1Def}>
                      <ValueMetric data={metric1} t={t} />
                    </EditableSlotGroup>
                    <EditableSlotGroup groupPath="investorscopycontent.value_metric_2" definition={valueMetric2Def}>
                      <ValueMetric data={metric2} t={t} />
                    </EditableSlotGroup>
                    <EditableSlotGroup groupPath="investorscopycontent.value_metric_3" definition={valueMetric3Def}>
                      <ValueMetric data={metric3} t={t} />
                    </EditableSlotGroup>
                  </>
                )}
              </ValueAddSimulator>
            <RichTextContent
              path="investorscopycontent.chart_footnote"
              label="Chart Footnote"
              as="p"
              className="mt-6 font-body text-xs leading-relaxed text-[rgb(var(--ala-on-primary))]/45"
            />
          </div>
        </div>
      </section>

      {/* Footprint */}
      <section className="w-full bg-[rgb(var(--ala-surface))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,6fr)_minmax(0,6fr)] lg:items-end">
            <div>
              <Eyebrow>
                <RichTextContent
                  path="investorscopycontent.footprint_eyebrow"
                  label="Footprint Eyebrow"
                  as="span"
                  className={`${EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
                />
              </Eyebrow>
              <RichTextContent
                path="investorscopycontent.footprint_headline"
                label="Footprint Headline"
                as="h2"
                className={`${H2_CLASS} text-[rgb(var(--ala-text))]`}
              />
              <RichTextContent
                path="investorscopycontent.footprint_body"
                label="Footprint Body"
                as="p"
                className="mt-5 max-w-[620px] font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-[rgb(var(--ala-background))] p-6">
                <RichTextContent
                  path="investorscopycontent.footprint_markets_value"
                  label="Markets Value"
                  as="span"
                  className="block font-heading text-[clamp(44px,4.4vw,64px)] font-semibold leading-none tracking-[-0.03em] text-[rgb(var(--ala-text))]"
                />
                <RichTextContent
                  path="investorscopycontent.footprint_markets_label"
                  label="Markets Label"
                  as="span"
                  className="mt-2 block font-heading text-sm text-[rgb(var(--ala-text-muted))]"
                />
              </div>
              <div className="rounded-2xl bg-[rgb(var(--ala-background))] p-6">
                <RichTextContent
                  path="investorscopycontent.footprint_states_value"
                  label="States Value"
                  as="span"
                  className="block font-heading text-[clamp(44px,4.4vw,64px)] font-semibold leading-none tracking-[-0.03em] text-[rgb(var(--ala-text))]"
                />
                <RichTextContent
                  path="investorscopycontent.footprint_states_label"
                  label="States Label"
                  as="span"
                  className="mt-2 block font-heading text-sm text-[rgb(var(--ala-text-muted))]"
                />
              </div>
            </div>
          </div>

          <div className="mt-12 overflow-hidden rounded-3xl bg-[rgb(var(--ala-primary))] p-5 md:p-8">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
              <div className="flex flex-col">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <RichTextContent
                    path="investorscopycontent.map_hint"
                    label="Map Hint"
                    as="span"
                    className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]/50"
                  />
                  {selectedState ? (
                    <button
                      type="button"
                      onClick={() => setSelectedState(null)}
                      className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-primary))]"
                    >
                      Show all
                    </button>
                  ) : null}
                </div>
                <div className="flex flex-1 items-center">
                  <FootprintMap points={mapPoints} focus={mapFocus} />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <EditableSlotGroup groupPath="investorscopycontent.state_1" definition={state1Def}>
                    <StateButton {...stateProps(state1)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_2" definition={state2Def}>
                    <StateButton {...stateProps(state2)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_3" definition={state3Def}>
                    <StateButton {...stateProps(state3)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_4" definition={state4Def}>
                    <StateButton {...stateProps(state4)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_5" definition={state5Def}>
                    <StateButton {...stateProps(state5)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_6" definition={state6Def}>
                    <StateButton {...stateProps(state6)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_7" definition={state7Def}>
                    <StateButton {...stateProps(state7)} />
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="investorscopycontent.state_8" definition={state8Def}>
                    <StateButton {...stateProps(state8)} />
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
            <div className="mt-8 rounded-2xl border border-[rgb(var(--ala-on-primary))]/10 p-6 md:flex md:items-start md:gap-10">
              <RichTextContent
                path="investorscopycontent.profile_title"
                label="Target Profile Title"
                as="h3"
                className="shrink-0 font-heading text-lg font-semibold text-[rgb(var(--ala-on-primary))] md:w-[220px]"
              />
              <RichTextContent
                path="investorscopycontent.profile_body"
                label="Target Profile List"
                as="div"
                className="mt-3 font-body text-sm leading-relaxed text-[rgb(var(--ala-on-primary))]/70 md:mt-0 [&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:before:mt-[0.55em] [&_li]:before:h-1.5 [&_li]:before:w-1.5 [&_li]:before:shrink-0 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_ul]:grid [&_ul]:list-none [&_ul]:gap-x-8 [&_ul]:gap-y-2 [&_ul]:sm:grid-cols-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* People-first bento */}
      <section className="w-full bg-[rgb(var(--ala-background))] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-4 px-6 md:px-[clamp(16px,3.3vw,48px)] lg:grid-cols-4 lg:grid-rows-2">
          <div className="group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-3xl bg-[rgb(var(--ala-primary))] p-8 md:p-10 lg:col-span-2 lg:row-span-2">
            <div className="absolute inset-0">
              <EditableSlotGroup groupPath="investorscopycontent.people_image" definition={peopleImageDef} className="block h-full w-full">
                <VibesitesImage
                  url={peopleImage.image || ''}
                  alt={peopleImage.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="h-full w-full object-cover opacity-70 transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            </div>
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgb(var(--ala-primary)/0.1)_0%,rgb(var(--ala-primary)/0.95)_75%)]" />
            <div className="relative z-10">
              <Eyebrow light>
                <RichTextContent
                  path="investorscopycontent.people_eyebrow"
                  label="People Eyebrow"
                  as="span"
                  className={`${EYEBROW_CLASS} text-[rgb(var(--ala-on-primary))]/80`}
                />
              </Eyebrow>
              <RichTextContent
                path="investorscopycontent.people_headline"
                label="People Headline"
                as="h2"
                className={`${H2_CLASS} max-w-[560px] text-[rgb(var(--ala-on-primary))] [&_em]:not-italic [&_em]:text-[rgb(var(--ala-secondary))]`}
              />
            </div>
          </div>
          <PeopleCard icon={<MapPin className="h-5 w-5" aria-hidden />}>
            <RichTextContent path="investorscopycontent.people_1_title" label="People 1 Title" as="h3" className="font-heading text-xl font-semibold text-[rgb(var(--ala-text))]" />
            <RichTextContent path="investorscopycontent.people_1_body" label="People 1 Body" as="p" className="font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]" />
          </PeopleCard>
          <PeopleCard icon={<Network className="h-5 w-5" aria-hidden />}>
            <RichTextContent path="investorscopycontent.people_2_title" label="People 2 Title" as="h3" className="font-heading text-xl font-semibold text-[rgb(var(--ala-text))]" />
            <RichTextContent path="investorscopycontent.people_2_body" label="People 2 Body" as="p" className="font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]" />
          </PeopleCard>
          <PeopleCard icon={<Users className="h-5 w-5" aria-hidden />}>
            <RichTextContent path="investorscopycontent.people_3_title" label="People 3 Title" as="h3" className="font-heading text-xl font-semibold text-[rgb(var(--ala-text))]" />
            <RichTextContent path="investorscopycontent.people_3_body" label="People 3 Body" as="p" className="font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]" />
          </PeopleCard>
          <PeopleCard icon={<HeartHandshake className="h-5 w-5" aria-hidden />}>
            <RichTextContent path="investorscopycontent.people_4_title" label="People 4 Title" as="h3" className="font-heading text-xl font-semibold text-[rgb(var(--ala-text))]" />
            <RichTextContent path="investorscopycontent.people_4_body" label="People 4 Body" as="p" className="font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]" />
          </PeopleCard>
        </div>
      </section>

      {/* 30-year timeline */}
      <section className="w-full bg-[rgb(var(--ala-dark))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
          <div className="mb-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="max-w-[760px]">
              <Eyebrow light>
                <RichTextContent
                  path="investorscopycontent.timeline_eyebrow"
                  label="Timeline Eyebrow"
                  as="span"
                  className={`${EYEBROW_CLASS} text-[rgb(var(--ala-on-primary))]/70`}
                />
              </Eyebrow>
              <RichTextContent
                path="investorscopycontent.timeline_headline"
                label="Timeline Headline"
                as="h2"
                className={`${H2_CLASS} text-[rgb(var(--ala-on-primary))]`}
              />
              <RichTextContent
                path="investorscopycontent.timeline_body"
                label="Timeline Body"
                as="p"
                className="mt-5 font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <PortalButton data={portal} variant="outline-light" />
          </div>

          <Timeline
            milestones={[milestone1, milestone2, milestone3, milestone4, milestone5, milestone6]}
            renderYearButton={(i, button) => {
              if (i === 0)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_1" definition={milestone1Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 1)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_2" definition={milestone2Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 2)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_3" definition={milestone3Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 3)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_4" definition={milestone4Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 4)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_5" definition={milestone5Def}>
                    {button}
                  </EditableSlotGroup>
                );
              return (
                <EditableSlotGroup key={i} groupPath="investorscopycontent.milestone_6" definition={milestone6Def}>
                  {button}
                </EditableSlotGroup>
              );
            }}
          />
        </div>
      </section>

      {/* Capital partners + investor contact form */}
      <section id="investor-contact" className="w-full scroll-mt-24 bg-[rgb(var(--ala-surface))] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:px-[clamp(16px,3.3vw,48px)] lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-16">
          <div className="flex flex-col">
            <Eyebrow>
              <RichTextContent
                path="investorscopycontent.partners_eyebrow"
                label="Partners Eyebrow"
                as="span"
                className={`${EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
              />
            </Eyebrow>
            <RichTextContent
              path="investorscopycontent.partners_headline"
              label="Partners Headline"
              as="h2"
              className={`${H2_CLASS} text-[rgb(var(--ala-text))]`}
            />
            <RichTextContent
              path="investorscopycontent.partners_body"
              label="Partners Body"
              as="p"
              className="mt-5 font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))]"
            />
            <RichTextContent
              path="investorscopycontent.partners_list"
              label="Capital Partner Types"
              as="div"
              className="mt-8 font-heading text-lg font-medium text-[rgb(var(--ala-text))] [&_li]:flex [&_li]:items-center [&_li]:gap-4 [&_li]:border-t [&_li]:border-[rgb(var(--ala-border))] [&_li]:py-4 [&_li]:transition-all [&_li]:duration-300 [&_li]:before:h-2 [&_li]:before:w-2 [&_li]:before:shrink-0 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_li]:before:transition-all hover:[&_li]:pl-2 hover:[&_li]:text-[rgb(var(--ala-secondary))] [&_ul]:list-none [&_ul]:border-b [&_ul]:border-[rgb(var(--ala-border))]"
            />
            <div
              onMouseMove={trackSpotlight}
              className="group relative mt-10 flex flex-col gap-4 overflow-hidden rounded-3xl bg-[rgb(var(--ala-primary))] p-6 sm:flex-row sm:items-center sm:justify-between md:p-7"
            >
              <Spotlight />
              <RichTextContent
                path="investorscopycontent.portal_prompt"
                label="Portal Prompt"
                as="span"
                className="relative z-10 font-heading text-lg font-medium text-[rgb(var(--ala-on-primary))]"
              />
              <div className="relative z-10">
                <PortalButton data={portal} />
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-[rgb(var(--ala-background))] p-7 shadow-[0_20px_60px_rgba(10,22,38,0.08)] md:p-10">
            <RichTextContent
              path="investorscopycontent.form_heading"
              label="Form Heading"
              as="h3"
              className="font-heading text-[clamp(24px,2.2vw,32px)] font-semibold text-[rgb(var(--ala-text))]"
            />
            <RichTextContent
              path="investorscopycontent.form_intro"
              label="Form Intro"
              as="p"
              className="mb-8 mt-2 font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]"
            />
            <div className="sr-only">
              <EditableToken path="investorscopycontent.form_email" as="span">
                {formEmail}
              </EditableToken>
            </div>
            <InvestorContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InvestorsCopyContent',
  displayName: 'Investors Copy Content',
  tokens: {
    'investorscopycontent.strategy_eyebrow': {
      label: 'Strategy Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Our Investment Approach</p>',
    },
    'investorscopycontent.strategy_headline': {
      label: 'Strategy Headline',
      type: 'richtext',
      defaultValue: '<p>Value-add. Sun Belt. <em>Multifamily.</em></p>',
    },
    'investorscopycontent.strategy_intro': {
      label: 'Strategy Intro',
      type: 'richtext',
      defaultValue:
        '<p>For 30 years, we have focused on one thing: creating genuine value for the communities we own — and the people who live and invest in them.</p>',
    },
    'investorscopycontent.who_title': {
      label: 'Who We Are Title',
      type: 'richtext',
      defaultValue: '<p>Who We Are</p>',
    },
    'investorscopycontent.who_body': {
      label: 'Who We Are Body',
      type: 'richtext',
      defaultValue:
        '<p>A <strong>vertically-integrated, value-add owner-operator</strong> with 30 years of multifamily experience. We own, renovate, and manage every community ourselves — no third-party management, no divided interests.</p>',
    },
    'investorscopycontent.what_title': {
      label: 'What We Do Title',
      type: 'richtext',
      defaultValue: '<p>What We Do</p>',
    },
    'investorscopycontent.what_body': {
      label: 'What We Do Body',
      type: 'richtext',
      defaultValue:
        '<p>We acquire well-located, <strong>mid-priced multifamily communities</strong> and make them better places to live — through thoughtful renovations, a resident-first approach, and hands-on, in-house operations.</p>',
    },
    'investorscopycontent.how_title': {
      label: 'Our Strategy Title',
      type: 'richtext',
      defaultValue: '<p>Our Strategy</p>',
    },
    'investorscopycontent.how_body': {
      label: 'Our Strategy Body',
      type: 'richtext',
      defaultValue:
        '<p><strong>Value-add multifamily in high-growth Sun Belt markets</strong>, guided by deep market experience and hyperlocal submarket intelligence. Operation, not allocation: we create value by running the asset — not just funding it.</p>',
    },
    'investorscopycontent.value_eyebrow': {
      label: 'Value-Add Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Value-Add Spotlight</p>',
    },
    'investorscopycontent.value_headline': {
      label: 'Value-Add Headline',
      type: 'richtext',
      defaultValue: '<p>Better communities. <em>Stronger performance.</em></p>',
    },
    'investorscopycontent.value_body': {
      label: 'Value-Add Body',
      type: 'richtext',
      defaultValue:
        '<p>When residents are better served, communities perform better. Our value-add playbook invests in the homes, amenities, and service that residents feel every day — and that shows up in the numbers.</p>',
    },
    'investorscopycontent.value_levers': {
      label: 'Value-Add Levers',
      type: 'richtext',
      defaultValue:
        '<ul><li><strong>Interior renovations</strong> that modernize kitchens, baths, and finishes.</li><li><strong>Amenity and curb-appeal upgrades</strong> residents are proud to come home to.</li><li><strong>In-house operations</strong> and revenue management that tighten execution.</li></ul>',
    },
    'investorscopycontent.chart_title': {
      label: 'Chart Title',
      type: 'richtext',
      defaultValue: '<p>The value-add business plan, in motion</p>',
    },
    'investorscopycontent.value_hint': {
      label: 'Chart Hint',
      type: 'richtext',
      defaultValue: '<p>Drag the slider or tap a stage to watch a community transform.</p>',
    },
    'investorscopycontent.value_stages': {
      label: 'Plan Stages (separate with |)',
      type: 'text',
      defaultValue: 'Acquire | Renovate | Lease-up | Stabilize',
    },
    'investorscopycontent.chart_before_label': {
      label: 'Chart Start Label',
      type: 'richtext',
      defaultValue: '<p>At acquisition</p>',
    },
    'investorscopycontent.chart_after_label': {
      label: 'Chart End Label',
      type: 'richtext',
      defaultValue: '<p>Stabilized</p>',
    },
    'investorscopycontent.chart_footnote': {
      label: 'Chart Footnote',
      type: 'richtext',
      defaultValue:
        '<p>Figures shown are illustrative of the value-add business plan. Past performance is not indicative of future results.</p>',
    },
    'investorscopycontent.footprint_eyebrow': {
      label: 'Footprint Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Footprint Overview</p>',
    },
    'investorscopycontent.footprint_headline': {
      label: 'Footprint Headline',
      type: 'richtext',
      defaultValue: '<p>Boots on the ground across the Sun Belt.</p>',
    },
    'investorscopycontent.footprint_body': {
      label: 'Footprint Body',
      type: 'richtext',
      defaultValue:
        '<p>Our teams live and work in the markets we invest in. That presence gives us hyperlocal submarket intelligence — and the ability to act on it quickly.</p>',
    },
    'investorscopycontent.footprint_markets_value': {
      label: 'Markets Value',
      type: 'richtext',
      defaultValue: '<p>20</p>',
    },
    'investorscopycontent.footprint_markets_label': {
      label: 'Markets Label',
      type: 'richtext',
      defaultValue: '<p>Active markets</p>',
    },
    'investorscopycontent.footprint_states_value': {
      label: 'States Value',
      type: 'richtext',
      defaultValue: '<p>8</p>',
    },
    'investorscopycontent.footprint_states_label': {
      label: 'States Label',
      type: 'richtext',
      defaultValue: '<p>States</p>',
    },
    'investorscopycontent.map_hint': {
      label: 'Map Hint',
      type: 'richtext',
      defaultValue: '<p>Select a state · hover a dot to explore a community</p>',
    },
    'investorscopycontent.profile_title': {
      label: 'Target Profile Title',
      type: 'richtext',
      defaultValue: '<p>Target asset profile</p>',
    },
    'investorscopycontent.profile_body': {
      label: 'Target Profile List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Mid-priced, value-add multifamily communities</li><li>Generally built in the late 1990s or later</li><li>High-growth Sun Belt submarkets with strong job and population growth</li><li>Clear opportunity to improve through renovation and operations</li></ul>',
    },
    'investorscopycontent.people_eyebrow': {
      label: 'People Eyebrow',
      type: 'richtext',
      defaultValue: '<p>A People-Centric Platform</p>',
    },
    'investorscopycontent.people_headline': {
      label: 'People Headline',
      type: 'richtext',
      defaultValue: '<p>Putting people first is how we create <em>lasting value.</em></p>',
    },
    'investorscopycontent.people_1_title': {
      label: 'People 1 Title',
      type: 'richtext',
      defaultValue: '<p>Boots on the ground</p>',
    },
    'investorscopycontent.people_1_body': {
      label: 'People 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Deep market experience and hyperlocal submarket intelligence from teams who know every neighborhood we invest in.</p>',
    },
    'investorscopycontent.people_2_title': {
      label: 'People 2 Title',
      type: 'richtext',
      defaultValue: '<p>Vertically-integrated</p>',
    },
    'investorscopycontent.people_2_body': {
      label: 'People 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Acquisitions, renovation, revenue management, and property management under one roof — so strategy and execution never drift apart.</p>',
    },
    'investorscopycontent.people_3_title': {
      label: 'People 3 Title',
      type: 'richtext',
      defaultValue: '<p>Resident-first approach</p>',
    },
    'investorscopycontent.people_3_body': {
      label: 'People 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Great service and well-cared-for homes keep residents longer. Happier residents build stronger communities — and more durable returns.</p>',
    },
    'investorscopycontent.people_4_title': {
      label: 'People 4 Title',
      type: 'richtext',
      defaultValue: '<p>Complete investor alignment</p>',
    },
    'investorscopycontent.people_4_body': {
      label: 'People 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>No third-party managers and no divided interests. We operate what we own, so our incentives stay aligned with our partners.</p>',
    },
    'investorscopycontent.timeline_eyebrow': {
      label: 'Timeline Eyebrow',
      type: 'richtext',
      defaultValue: '<p>30-Year Track Record</p>',
    },
    'investorscopycontent.timeline_headline': {
      label: 'Timeline Headline',
      type: 'richtext',
      defaultValue: '<p>Three decades of building communities.</p>',
    },
    'investorscopycontent.timeline_body': {
      label: 'Timeline Body',
      type: 'richtext',
      defaultValue:
        '<p>Tap through the moments that shaped us — from our first communities to five funds and approximately $7 billion in gross asset value.</p>',
    },
    'investorscopycontent.partners_eyebrow': {
      label: 'Partners Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Capital Partners</p>',
    },
    'investorscopycontent.partners_headline': {
      label: 'Partners Headline',
      type: 'richtext',
      defaultValue: '<p>Built for long-term partnerships.</p>',
    },
    'investorscopycontent.partners_body': {
      label: 'Partners Body',
      type: 'richtext',
      defaultValue:
        '<p>Whether you are a current investor or exploring a new relationship, our capital markets team is here to help. We partner with:</p>',
    },
    'investorscopycontent.partners_list': {
      label: 'Capital Partner Types',
      type: 'richtext',
      defaultValue:
        '<ul><li>Public &amp; Private Pensions</li><li>Multi-Family Offices</li><li>Single-Family Offices</li><li>High-Net-Worth Individuals (HNWIs)</li></ul>',
    },
    'investorscopycontent.portal_prompt': {
      label: 'Portal Prompt',
      type: 'richtext',
      defaultValue: '<p>Already an investor?</p>',
    },
    'investorscopycontent.form_heading': {
      label: 'Form Heading',
      type: 'richtext',
      defaultValue: '<p>Connect with Capital Markets</p>',
    },
    'investorscopycontent.form_intro': {
      label: 'Form Intro',
      type: 'richtext',
      defaultValue:
        '<p>Tell us a bit about you and a member of our investor relations team will follow up personally.</p>',
    },
    'investorscopycontent.form_email': {
      label: 'Form Fallback Email',
      type: 'email',
      defaultValue: 'info@alapts.com',
    },
  },
  slotGroups: {
    'investorscopycontent.portal_cta': portalCtaDef,
    'investorscopycontent.strategy_link': strategyLinkDef,
    'investorscopycontent.panel_image_1': panelImage1Def,
    'investorscopycontent.panel_image_2': panelImage2Def,
    'investorscopycontent.panel_image_3': panelImage3Def,
    'investorscopycontent.people_image': peopleImageDef,
    'investorscopycontent.value_metric_1': valueMetric1Def,
    'investorscopycontent.value_metric_2': valueMetric2Def,
    'investorscopycontent.value_metric_3': valueMetric3Def,
    'investorscopycontent.state_1': state1Def,
    'investorscopycontent.state_2': state2Def,
    'investorscopycontent.state_3': state3Def,
    'investorscopycontent.state_4': state4Def,
    'investorscopycontent.state_5': state5Def,
    'investorscopycontent.state_6': state6Def,
    'investorscopycontent.state_7': state7Def,
    'investorscopycontent.state_8': state8Def,
    'investorscopycontent.milestone_1': milestone1Def,
    'investorscopycontent.milestone_2': milestone2Def,
    'investorscopycontent.milestone_3': milestone3Def,
    'investorscopycontent.milestone_4': milestone4Def,
    'investorscopycontent.milestone_5': milestone5Def,
    'investorscopycontent.milestone_6': milestone6Def,
  },
};

(InvestorsCopyContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InvestorsCopyContent;
